import React from 'react'
import classNames from 'classnames'
import { IconBulb, IconFileTypePdf } from '@tabler/icons-react'
import { format } from 'date-fns'
import Markdown from 'marked-react'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { Accordion } from '@/components/accordions/Accordion'
import { Card } from '@/components/cards/Card'
import { Table } from '@/components/tables/Table/Table'
import { markdownLinkRenderer } from '@/utils/helpers'

export const SECTION_TYPES = {
  EXECUTIVE_SUMMARY: 'executive_summary',
  OVERALL_ASSESSMENT: 'overall_assessment',
  CALL_SEGMENT_OPPORTUNITIES: 'call_segment_opportunities',
  CALL_TYPE_OPPORTUNITIES: 'call_type_opportunities',
}

const markdownRenderer = {
  ...markdownLinkRenderer,
  table: (children) => (
    <div className="table-scroll wrap">
      <Table celled className="full-width">
        {children}
      </Table>
    </div>
  ),
  tableCell: (children, flags) => {
    const Tag = flags.header ? 'th' : 'td'
    const alignClass = classNames('text-left', {
      [`text-${flags.align}`]: Boolean(flags.align),
    })
    return <Tag className={alignClass}>{children}</Tag>
  },
}

const AutomationInsightsSummary = ({ summaryCards }) => {
  return (
    <div className="flex flex-wrap medium-gap mb">
      {summaryCards.map((card) => (
        <div key={card.label} className="flex-item mb" style={{ minWidth: 0 }}>
          <Card compact className="full-height">
            <div className="flex-column small-gap">
              <div className="muted-text text-overflow full-width no-wrap" title={card.label}>
                {card.label}
              </div>
              <div className="bold text-overflow full-width no-wrap" title={card.value}>
                {card.value}
              </div>
            </div>
          </Card>
        </div>
      ))}
    </div>
  )
}

const renderMarkdown = (content) => (
  <Markdown renderer={markdownRenderer} gfm breaks>
    {content}
  </Markdown>
)

const renderExecutiveSummary = (content) => {
  if (!content) {
    return null
  }
  return (
    <div>
      {renderMarkdown(content.methodology)}

      {content.key_findings && (
        <>
          <h3>Key Findings</h3>
          {renderMarkdown(content.key_findings)}
        </>
      )}

      {content.recommendations && (
        <>
          <h3>Recommendations</h3>
          {renderMarkdown(content.recommendations)}
        </>
      )}
    </div>
  )
}

const renderOverallAssessment = (content) => renderMarkdown(content)

const renderOpportunity = (opp, sectionType) => {
  return (
    <div key={opp.name} className="mb">
      <h3>{opp.name}</h3>
      <p>
        <strong>Description:</strong> {opp.description}
      </p>

      {sectionType === SECTION_TYPES.CALL_SEGMENT_OPPORTUNITIES && (
        <p>
          <strong>Applies to:</strong> {opp.applies_to}
        </p>
      )}

      {sectionType === SECTION_TYPES.CALL_TYPE_OPPORTUNITIES && (
        <>
          <p>
            <strong>Frequency:</strong> {opp.frequency}
          </p>
          {opp.can_fully_automate !== null && opp.can_fully_automate !== undefined && (
            <>
              <p>
                <strong>Can fully automate:</strong> {opp.can_fully_automate ? 'Yes' : 'No'}
              </p>
              {!opp.can_fully_automate && (
                <p>
                  <strong>Automatable portion:</strong> {opp.automatable_portion}
                </p>
              )}
            </>
          )}
        </>
      )}

      {!isEmpty(opp.voice_agent_steps) && (
        <>
          <p>
            <strong>Voice Agent Steps:</strong>
          </p>
          <ul>
            {opp.voice_agent_steps.map((step) => (
              <li key={`${opp.name}-${step}`}>{step}</li>
            ))}
          </ul>
        </>
      )}

      <p>
        <strong>Handoff Trigger:</strong> {opp.handoff_trigger}
      </p>
      <p>
        <strong>Handoff Context:</strong> {opp.handoff_context}
      </p>

      {!isEmpty(opp.required_apis) && (
        <p>
          <strong>Required APIs:</strong> {opp.required_apis.join(', ')}
        </p>
      )}

      {!isEmpty(opp.challenges) && (
        <>
          <p>
            <strong>Challenges:</strong>
          </p>
          <ul>
            {opp.challenges.map((challenge) => (
              <li key={`${opp.name}-${challenge}`}>{challenge}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

const renderOpportunities = (section) => {
  if (isEmpty(section.opportunities)) {
    return (
      <div>
        <p className="muted-text">{section.description}</p>
        {renderMarkdown(section.empty_message || 'No opportunities identified.')}
      </div>
    )
  }

  return (
    <div>
      <p className="muted-text">{section.description}</p>
      {section.opportunities.map((opp) => renderOpportunity(opp, section.type))}
    </div>
  )
}

export const formatSummaryCards = ({
  report,
  selectedOrganization,
  selectedPlaybooks,
  selectedAgents,
  startDate,
  endDate,
  organizationOptions,
}) => {
  const selectedOrgLabel =
    organizationOptions.find((option) => option.value === Number(selectedOrganization))?.label ||
    'Selected Organization'

  const selectedPlaybookLabel = isEmpty(selectedPlaybooks)
    ? 'All Playbooks'
    : selectedPlaybooks.length === 1
      ? selectedPlaybooks[0].label
      : `${selectedPlaybooks.length} Playbooks`

  const selectedAgentsLabel = isEmpty(selectedAgents)
    ? 'All Agents'
    : `${selectedAgents.length} ${selectedAgents.length === 1 ? 'Agent' : 'Agents'}`

  const selectedDateLabel =
    startDate && endDate
      ? `${format(new Date(startDate), 'MMM d, yyyy')} - ${format(new Date(endDate), 'MMM d, yyyy')}`
      : ''

  return [
    { label: 'Calls analyzed', value: report?.callCount?.toLocaleString() || '--' },
    { label: 'Company', value: selectedOrgLabel },
    { label: 'Playbook', value: selectedPlaybookLabel },
    { label: 'Agents', value: selectedAgentsLabel },
    { label: 'Time Frame', value: selectedDateLabel },
  ]
}

export const getApiUrl = (apiService) => {
  if (!apiService.reporting) {
    return ''
  }
  return `${apiService.reporting}/api/automation-insights`
}

export const AutomationInsightsReport = ({
  report,
  summaryCards,
  onExport,
  isExporting,
  canExport,
  expandedSections,
  onToggleSection,
}) => {
  const reportSections = report?.sections || []
  const renderSectionContent = (section) => {
    if (section.type === SECTION_TYPES.EXECUTIVE_SUMMARY) {
      return (
        <>
          <AutomationInsightsSummary summaryCards={summaryCards} />
          {renderExecutiveSummary(section.content)}
        </>
      )
    }

    switch (section.type) {
      case SECTION_TYPES.OVERALL_ASSESSMENT:
        return renderOverallAssessment(section.content)
      case SECTION_TYPES.CALL_SEGMENT_OPPORTUNITIES:
      case SECTION_TYPES.CALL_TYPE_OPPORTUNITIES:
        return renderOpportunities(section)
      default:
        return <div>Unknown section type: {section.type}</div>
    }
  }

  return (
    <>
      {reportSections.map((section) => {
        const isExpanded = expandedSections[section.type]
        return (
          <div key={section.type} className="module-card">
            <Accordion
              id={section.type}
              title={section.title}
              large
              icon={
                <IconBulb
                  className={classNames('tabler-icon tabler-icon-bulb', {
                    'status-warning': isExpanded,
                    'status-magical': !isExpanded,
                  })}
                />
              }
              actions={
                section.type === SECTION_TYPES.EXECUTIVE_SUMMARY ? (
                  <Button
                    onClick={(event) => {
                      onExport()
                      event.stopPropagation()
                    }}
                    disabled={!canExport || isExporting}
                    loading={isExporting}
                    className="no-wrap"
                    icon={<IconFileTypePdf />}
                    data-testid="automation-insights-export"
                  >
                    Export PDF
                  </Button>
                ) : null
              }
              expanded={isExpanded}
              handleToggle={onToggleSection}
            >
              <div className="mt">{renderSectionContent(section)}</div>
            </Accordion>
          </div>
        )
      })}
    </>
  )
}
